/**
 * @fileoverview The portfolio's tile primitives, shared by the completed
 * assignments, the open slots, and the creative projects section. Editorial
 * by intent: the app screenshots are the page, framed by thin rules and
 * strong type, with the newest tool set as a wide feature. Colors derive
 * from the course theme variables (see `course/contexts/themeUtils.ts`).
 */

import {Link} from 'react-router-dom';
import styled, {css, keyframes} from 'styled-components';

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Entrance: a short fade-up, staggered by the rise-delay custom property set per tile. */
const entrance = css`
  /* backwards, not both: a lingering transform would re-anchor fixed descendants. */
  animation: ${rise} 420ms ease-out backwards;
  animation-delay: var(--rise-delay, 0ms);
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** Thin rule color used by every frame on the page. */
export const RULE = 'color-mix(in srgb, var(--course-fg, #111827) 16%, transparent)';

/** The tile grid: every tile the same size. */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 32px;
`;

/** Positions a tile's controls (the owner's edit link) over the card link. */
export const TileFrame = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  ${entrance}
`;

/** The owner's way back into the assignment, floated over the thumbnail. */
export const EditLink = styled(Link)`
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 9px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.82);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  &:hover {
    color: #fff;
    background: #111827;
    text-decoration: none;
  }
`;

/**
 * A finished tool: its screenshot edge to edge in a thin frame, the caption
 * below.
 */
export const TileLink = styled(Link)`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid ${RULE};
  border-radius: 14px;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  transition:
    transform 200ms ease,
    border-color 200ms ease,
    box-shadow 200ms ease;
  &:hover {
    color: inherit;
    text-decoration: none;
    transform: translateY(-3px);
    border-color: color-mix(in srgb, var(--course-fg, #111827) 34%, transparent);
    box-shadow: 0 18px 40px -24px rgba(0, 0, 0, 0.5);
  }
`;

/** The screenshot well. */
export const Thumb = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  background: #f3f4f6;
  overflow: hidden;
  border-bottom: 1px solid ${RULE};
`;

export const ThumbImage = styled.img<{$stale: boolean}>`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  opacity: ${({$stale}) => ($stale ? 0.6 : 1)};
  transition: opacity 300ms ease;
`;

export const ThumbPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
`;

/** The finished mark. */
export const Completed = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #16a34a;
  color: #fff;
`;

/** The caption under the screenshot. */
export const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px 16px;
`;

export const TileTitle = styled.div`
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.005em;
  line-height: 1.1;
`;

/** Small caps caption line: the date, a state, a call to action. */
export const TileMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--course-fg, #111827) 60%, transparent);
`;

/** An open slot: the same frame, dashed, waiting for its tool. */
export const SlotLink = styled(Link)`
  display: flex;
  flex-direction: column;
  border: 1.5px dashed ${RULE};
  border-radius: 14px;
  overflow: hidden;
  color: inherit;
  text-decoration: none;
  transition:
    transform 200ms ease,
    border-color 200ms ease;
  ${entrance}
  &:hover {
    color: inherit;
    text-decoration: none;
    transform: translateY(-3px);
    border-color: color-mix(in srgb, var(--course-fg, #111827) 45%, transparent);
  }
`;

/** The slot's empty well, with the start mark centered. */
export const SlotThumb = styled.div`
  aspect-ratio: 4 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1.5px dashed ${RULE};
`;

/** The start mark. */
export const SlotMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px dashed currentColor;
  opacity: 0.55;
  ${SlotLink}:hover & {
    opacity: 0.9;
  }
`;

/** The slot's caption line; same voice as {@link TileMeta}. */
export const SlotMeta = styled(TileMeta)``;
